import { motion } from "framer-motion";
import { Camera, FileText } from "lucide-react";

// Fotos y textos reales del proceso en taller.
const STEPS = [
  {
    num: "01",
    title: "Diagnóstico en Planta",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-shields/artifacts/bd5b4a8ad77f905c_1.webp",
    text: "Intervenimos directo en tu centro de maquinado para evaluar el estado real de guías y patines, detectando riesgos de acumulación de viruta y colisión.",
  },
  {
    num: "02",
    title: "Extracción y Evaluación",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-shields/artifacts/ba39ec9b96d19d5d_2.webp",
    text: "Retiramos los sistemas dañados para su análisis dimensional milimétrico y trazo de geometría original en taller.",
  },
  {
    num: "03",
    title: "Solución y Reconstrucción",
    photo: "https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-shields/artifacts/00633e43d7da8313_3.webp",
    text: "Sustituimos lamelas, wipers y componentes de desgaste para asegurar un desplazamiento suave y sin paros imprevistos.",
  },
];

export default function RepairWorkshopSteps() {
  return (
    <section
      id="proceso-taller"
      data-testid="repair-workshop-section"
      className="gf-section bg-[#0A1B35] relative overflow-hidden"
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00B4D8]/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-[#00B4D8]" />
            <span className="gf-overline">Proceso en taller</span>
          </div>
          <h2
            data-testid="workshop-title"
            className="font-display font-black text-white text-2xl sm:text-3xl lg:text-5xl tracking-tighter leading-[1.08] uppercase max-w-4xl"
          >
            Diagnóstico, Extracción y Reconstrucción{" "}
            <span className="text-[#00B4D8]">en Taller</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((s, i) => (
            <motion.article
              key={s.num}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`workshop-step-${s.num}`}
              className="bg-[#061226] border border-white/10 hover:border-[#00B4D8]/50 transition-colors duration-500 p-6 lg:p-7 flex flex-col"
              style={{ borderRadius: "2px" }}
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono-accent text-[11px] tracking-[0.3em] text-[#00B4D8] uppercase">
                  Paso {s.num}
                </span>
                <span className="font-display font-black text-4xl gf-text-outline leading-none select-none" aria-hidden="true">
                  {s.num}
                </span>
              </div>

              <h3 className="font-display font-bold text-white text-xl tracking-tight mb-5">
                {s.title}
              </h3>

              {/* Espacio reservado para foto */}
              {s.photo ? (
                <img
                  src={s.photo}
                  alt={`${s.title} — Guarda Flex`}
                  className="w-full aspect-video object-cover mb-5"
                  style={{ borderRadius: "2px" }}
                />
              ) : (
                <div
                  data-testid={`workshop-photo-slot-${s.num}`}
                  className="w-full aspect-video border border-dashed border-white/20 bg-[#0A1B35] flex flex-col items-center justify-center gap-2 mb-5"
                  style={{ borderRadius: "2px" }}
                >
                  <Camera size={20} className="text-white/30" />
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/35">
                    Espacio para foto
                  </span>
                </div>
              )}

              {/* Espacio reservado para descripción */}
              {s.text ? (
                <p className="text-white/60 text-sm leading-relaxed">{s.text}</p>
              ) : (
                <div
                  data-testid={`workshop-text-slot-${s.num}`}
                  className="flex items-center gap-2 text-white/30 text-xs"
                >
                  <FileText size={13} />
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em]">
                    Espacio para descripción
                  </span>
                </div>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
